// @ts-strict-ignore
import * as React from 'react';

import { InfoCircleOutlined } from '@ant-design/icons';
import Input from 'antd/lib/input';
import Modal from 'antd/lib/modal';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import { antGrey } from '../styles/globalStyles';
import {
    GeneratedReleaseNotesMarkdownContext,
    GeneratedReleaseNotesStepContext,
    GeneratedReleaseNotesType,
    IntegrationType,
    LocalizedBetaTestingNotes,
    RunwayResponseType,
} from '../../types';
import Form from 'antd/lib/form';
import Tabs from 'antd/lib/tabs';
import { tryUpdateRelease, CreateOrUpdateBetaBuildLocalizationsRequest } from '../../actions/release';
import { useDispatch } from 'react-redux';
import { ReleaseUpdatable } from '../../actions/updatable';
import { isFirebaseBetaIntegration, isGoogleBetaIntegration, isTestFlightBetaIntegration } from '../../helpers';
import message from 'antd/lib/message';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';
import { GenerateReleaseNotesDropdown } from '../sharedComponents/GenerateReleaseNotesDropdown';

interface BetaWhatToTestDialogProps extends ReleaseComponentProps {
    open: boolean;
    onCancel: () => void;
    onSaveSuccess: () => void;
    externalBuildId: string;
    betaNotes?: LocalizedBetaTestingNotes;
    androidBetaNotes?: LocalizedBetaTestingNotes[];
    betaIntegrationType: IntegrationType;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const { TextArea } = Input;

const BetaWhatToTestDialog = (props: BetaWhatToTestDialogProps): JSX.Element => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const [
        createOrUpdateLocalizationNotesForBuild,
        createOrUpdateLocalizationNotesForBuildIsLoading,
        createOrUpdateLocalizationNotesForBuildResponseType,
        createOrUpdateLocalizationNotesForBuildError,
    ] = useCallUpdatable(
        StepRunUpdatable.CreateOrUpdateLocalizationNotesForBuild,
        props.stepRunComponentProps,
        props.externalBuildId,
    );

    const [selectedPaneLocale, setSelectedPaneLocale] = React.useState(
        props.betaNotes?.locale || props.androidBetaNotes[0]?.locale || '',
    );

    const isUpdateLoadingReleaseStep = useIsLoading(ReleaseUpdatable.UpdateBetaBuildLocalization, props.releaseId);
    const isUpdateSuccessReleaseStep = useUpdated(ReleaseUpdatable.UpdateBetaBuildLocalization, props.releaseId);
    const updateErrorReleaseStep = useError(ReleaseUpdatable.UpdateBetaBuildLocalization, props.releaseId);

    const isUpdateLoading = isUpdateLoadingReleaseStep;
    const isUpdateSuccess = isUpdateSuccessReleaseStep;
    const updateError = updateErrorReleaseStep;

    const isMultiUpdateLoadingReleaseStep = useIsLoading(
        ReleaseUpdatable.UpdateMultiLocaleTestingNotes,
        props.releaseId,
    );
    const isMultiUpdateSuccessReleaseStep = useUpdated(ReleaseUpdatable.UpdateMultiLocaleTestingNotes, props.releaseId);
    const multiUpdateErrorReleaseStep = useError(ReleaseUpdatable.UpdateMultiLocaleTestingNotes, props.releaseId);

    const isMultiUpdateLoading = isMultiUpdateLoadingReleaseStep;
    const isMultiUpdateSuccess = isMultiUpdateSuccessReleaseStep;
    const multiUpdateError = multiUpdateErrorReleaseStep;

    const isAddLoadingReleaseStep = useIsLoading(ReleaseUpdatable.AddBetaBuildLocalizedNotes, props.releaseId);
    const isAddSuccessReleaseStep = useUpdated(ReleaseUpdatable.AddBetaBuildLocalizedNotes, props.releaseId);
    const addErrorReleaseStep = useError(ReleaseUpdatable.AddBetaBuildLocalizedNotes, props.releaseId);

    const isAddLoading =
        props.stepRunComponentProps ? createOrUpdateLocalizationNotesForBuildIsLoading : isAddLoadingReleaseStep;
    const isAddSuccess =
        props.stepRunComponentProps ?
            createOrUpdateLocalizationNotesForBuildResponseType === RunwayResponseType.Success
        :   isAddSuccessReleaseStep;
    const addError = props.stepRunComponentProps ? createOrUpdateLocalizationNotesForBuildError : addErrorReleaseStep;

    const onCancel = () => {
        props.onCancel();
    };

    const onSubmit = (values) => {
        if (
            isTestFlightBetaIntegration(props.betaIntegrationType) ||
            isFirebaseBetaIntegration(props.betaIntegrationType)
        ) {
            if (props.betaNotes) {
                const request: CreateOrUpdateBetaBuildLocalizationsRequest = {
                    orgId: props.orgId,
                    appId: props.appId,
                    releaseId: props.releaseId,
                    buildId: props.externalBuildId,
                    localizationId: props.betaNotes.id,
                    localizations: [
                        {
                            notes: values.betaNotes,
                            locale: props.betaNotes.locale,
                        },
                    ],
                };

                if (props.stepRunComponentProps) {
                    createOrUpdateLocalizationNotesForBuild(request);
                } else {
                    dispatch(tryUpdateRelease(ReleaseUpdatable.UpdateBetaBuildLocalization, request, props.releaseId));
                }
            } else {
                const request: CreateOrUpdateBetaBuildLocalizationsRequest = {
                    orgId: props.orgId,
                    appId: props.appId,
                    releaseId: props.releaseId,
                    buildId: props.externalBuildId,
                    localizations: [
                        {
                            notes: values.betaNotes,
                            locale: 'en-US',
                        },
                    ],
                };

                if (props.stepRunComponentProps) {
                    createOrUpdateLocalizationNotesForBuild(request);
                } else {
                    dispatch(tryUpdateRelease(ReleaseUpdatable.AddBetaBuildLocalizedNotes, request, props.releaseId));
                }
            }
        } else if (isGoogleBetaIntegration(props.betaIntegrationType)) {
            const localizations = props.androidBetaNotes.map((androidNote) => ({
                locale: androidNote.locale,
                notes: values[`note-${androidNote.locale}`],
            }));
            const request: CreateOrUpdateBetaBuildLocalizationsRequest = {
                orgId: props.orgId,
                appId: props.appId,
                buildId: props.externalBuildId,
                releaseId: props.releaseId,
                localizations,
            };

            if (props.stepRunComponentProps) {
                createOrUpdateLocalizationNotesForBuild(request);
            } else {
                dispatch(tryUpdateRelease(ReleaseUpdatable.UpdateMultiLocaleTestingNotes, request, props.releaseId));
            }
        } else {
            message.error('Unsupported beta integration!');
        }
    };

    React.useEffect(() => {
        if (props.open) {
            form.setFieldsValue({ betaNotes: props.betaNotes?.notes });
            props.androidBetaNotes.map((androidNote) => {
                form.setFieldsValue({
                    ['note-' + androidNote.locale]: androidNote.notes,
                });
            });
        }
    }, [props.open]);

    React.useEffect(() => {
        if (isUpdateSuccess) {
            globalMessage.success('Updated what to test notes!');

            props.onSaveSuccess();
        }
    }, [isUpdateSuccess]);

    React.useEffect(() => {
        if (isMultiUpdateSuccess) {
            globalMessage.success('Updated release notes!');

            props.onSaveSuccess();
        }
    }, [isMultiUpdateSuccess]);

    React.useEffect(() => {
        if (updateError) {
            globalMessage.error(updateError);
        }
    }, [updateError]);

    React.useEffect(() => {
        if (multiUpdateError) {
            globalMessage.error(multiUpdateError);
        }
    }, [multiUpdateError]);

    React.useEffect(() => {
        if (isAddSuccess) {
            globalMessage.success('Added what to test notes!');

            props.onSaveSuccess();
        }
    }, [isAddSuccess]);

    React.useEffect(() => {
        if (addError) {
            globalMessage.error(addError);
        }
    }, [addError]);

    const titleText = () => {
        switch (props.betaIntegrationType) {
            case IntegrationType.TestFlight:
                return 'What to test';
            case IntegrationType.GoogleBeta:
            case IntegrationType.FirebaseBeta:
                return 'Release notes';
            default:
                return null;
        }
    };

    return (
        <Modal
            open={props.open}
            title="Test information"
            onCancel={onCancel}
            onOk={() => form.submit()}
            okText="Save"
            okButtonProps={{ loading: isUpdateLoading || isAddLoading || isMultiUpdateLoading }}
            focusTriggerAfterClose={false}
            className="modal-with-background"
            width={'700px'}
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                {isTestFlightBetaIntegration(props.betaIntegrationType) && props.betaNotes?.displayLocale && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Text type="secondary" style={{ textAlign: 'right' }}>
                            {props.betaNotes.displayLocale ?? 'English (U.S)'}
                        </Text>
                    </div>
                )}
                <Form onFinish={onSubmit} form={form} layout="vertical" colon={false}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <Space>
                            <Text type="secondary">{titleText()}</Text>
                            <Tooltip
                                placement="topLeft"
                                title="Let your testers know what you would like them to test in this build. This information will be available to testers in all groups who have access to this build."
                            >
                                <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                            </Tooltip>
                        </Space>
                        <GenerateReleaseNotesDropdown
                            orgId={props.orgId}
                            appId={props.appId}
                            releaseId={props.releaseId}
                            stepContext={GeneratedReleaseNotesStepContext.BetaTesting}
                            markdownContext={GeneratedReleaseNotesMarkdownContext.MarkdownUnsupported}
                            locale={selectedPaneLocale}
                            disallowedTypes={[GeneratedReleaseNotesType.ReleaseNotesTypeTemplated]} // temporary, until we refactor the beta notes update routes.
                        />
                    </div>
                    {(isTestFlightBetaIntegration(props.betaIntegrationType) ||
                        isFirebaseBetaIntegration(props.betaIntegrationType)) && (
                        <Form.Item name="betaNotes">
                            <TextArea cols={100} rows={10} style={{ width: '650px' }} />
                        </Form.Item>
                    )}
                    {isGoogleBetaIntegration(props.betaIntegrationType) && (
                        <div className="multi-locale-tabs">
                            <Tabs
                                type="card"
                                onChange={(tabKey: string) => {
                                    setSelectedPaneLocale(tabKey);
                                }}
                            >
                                {props.androidBetaNotes?.map((betaNote) => (
                                    <Tabs.TabPane tab={betaNote.displayLocale} key={betaNote.locale}>
                                        <Form.Item name={`note-${betaNote.locale}`}>
                                            <TextArea rows={10} style={{ width: '650px' }} />
                                        </Form.Item>
                                    </Tabs.TabPane>
                                ))}
                            </Tabs>
                        </div>
                    )}
                </Form>
            </Space>
        </Modal>
    );
};

export default BetaWhatToTestDialog;
